import { submitLead } from "../../forms/submit-lead";
import React from "react";
import { CheckCircle, Pause, Play } from "lucide-react";
import { cn } from "../../lib/utils";
import { formatPhoneDisplay } from "../../lib/phone-formatter";
import { useSlideshow } from "../../lib/use-slideshow";
import { imagePositionStyle, type ImagePosition } from "../../lib/image-position";
import { imageMotionClass, type ImageMotion } from "../../lib/image-motion";
import { FRAME_ON_MEDIA_CLASS } from "../../lib/color-role";
import { type BaseComponentProps } from "../../types/puck";
import { getWindowJobsiteConfig } from "../../types/runtime-config";
import { useTurnstile } from "../../hooks/use-turnstile";
import { SmartLink } from "../smart-link";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { BookingWidgetInline } from "../booking-widget/booking-widget";
import { FormEmbed, FormEmbedSkeleton, useSavedContactForm } from "../../forms/form-embed";

export type HeroVariant = "clean" | "panel" | "proof";


interface HeroLeadCaptureCardProps {
  variant: HeroVariant;
  hasTrackingPhone?: boolean;
  /** Submit button label. Falls back to the resolved business CTA (set by render()). */
  submitLabel: string;
  /** Canonical business slug — the custom form's photo uploads resolve on it. */
  businessSlug?: string;
}


/** One photo in the showcase hero's rotation. */
export interface HeroPhoto {
  imageUrl: string;
  alt?: string;
  /** Where the photo keeps its focus when the screen crops it. */
  position?: ImagePosition;
}

export interface HeroProps extends BaseComponentProps {
  headline: string;
  subheadline: string;
  ctaText: string;
  ctaUrl: string;
  secondaryCtaText?: string;
  secondaryCtaUrl?: string;
  trustIndicators?: { text: string }[];
  backgroundImage?: string;
  /**
   * Photos the showcase layout cycles through. Falls back to `backgroundImage` when empty,
   * so a showcase hero with one photo is a static full-bleed hero rather than a broken
   * carousel.
   */
  photos?: HeroPhoto[];
  /** Showcase layout only: whether the headline, subheadline and buttons sit over the photo. */
  showCopy?: "yes" | "no";
  /** Showcase layout only: dot controls for flipping between photos. */
  showSlideshowDots?: "yes" | "no";
  /** Showcase layout only: a pause/play control for holding a photo. */
  showSlideshowPause?: "yes" | "no";
  /** Showcase layout only: how long each photo holds before fading. */
  slideSeconds?: number;
  /** Where the background image keeps its focus when the screen crops it. */
  imagePosition?: ImagePosition;
  /** Slow zoom-and-pan on the hero photography, or none. */
  imageMotion?: ImageMotion;
  overlay?: "dark" | "light" | "gradient";
  textColor?: "auto" | "light" | "dark";
  layout: "centered" | "split" | "showcase";
  variant?: HeroVariant;
  keywordBadge?: string;
  hasTrackingPhone?: boolean;
  /** Split-layout side card: "lead" (quick form) or "booking" (request flow). */
  heroForm?: "lead" | "booking";
  /** Booking-widget inputs (resolved from business metadata by hero.config). */
  bookingSlug?: string;
  bookingApiUrl?: string;
  businessName?: string;
}

function getOverlayStyles(overlay: HeroProps["overlay"]): string {
  const overlays: Record<NonNullable<HeroProps["overlay"]> | "primary", string> = {
    dark: "linear-gradient(to top, rgba(0, 0, 0, 0.74) 0%, rgba(0, 0, 0, 0.55) 45%, rgba(0, 0, 0, 0.3) 100%)",
    light:
      "linear-gradient(to right, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.3) 100%)",
    gradient:
      "linear-gradient(135deg, rgba(4, 24, 17, 0.86) 0%, rgba(18, 73, 44, 0.72) 55%, rgba(0, 0, 0, 0.42) 100%)",
    primary:
      "linear-gradient(to top, rgba(0, 0, 0, 0.74) 0%, rgba(0, 0, 0, 0.55) 45%, rgba(0, 0, 0, 0.3) 100%)",
  };

  return overlays[overlay || "dark"] || overlays.dark;
}

/**
 * Scrim behind showcase copy.
 *
 * Not getOverlayStyles(): those presets are tuned to cover a whole hero and bottom out at
 * 30% alpha, so a scrim that only covers part of the frame leaves a hard seam across the
 * photo. These reach full transparency, and follow the same overlay choice so the control
 * still means something in this layout.
 */
function getShowcaseScrimClass(overlay: HeroProps["overlay"]): string {
  switch (overlay) {
    case "light":
      return "jobsite:from-white/90 jobsite:via-white/45 jobsite:to-transparent";
    case "gradient":
      return "jobsite:from-primary/90 jobsite:via-primary/40 jobsite:to-transparent";
    case "dark":
    default:
      return "jobsite:from-black/80 jobsite:via-black/35 jobsite:to-transparent";
  }
}

function resolveLightText(
  textColor: HeroProps["textColor"],
  backgroundImage: string | undefined,
  overlay: HeroProps["overlay"],
): boolean {
  if (textColor === "light") return true;
  if (textColor === "dark") return false;
  if (!backgroundImage) return true;
  return overlay === "dark" || overlay === "gradient";
}


function HeroLeadCaptureCard({
  variant,
  hasTrackingPhone,
  submitLabel,
  businessSlug,
}: HeroLeadCaptureCardProps) {
  // A saved custom contact form takes over the card's field area (the same
  // form every surface renders); absent form = the built-in quick form below.
  // A skeleton holds the spot while the read resolves, so a saved form never
  // flashes the built-in fields first.
  const { loading: resolvingForm, data: customForm } = useSavedContactForm(true);
  const [formState, setFormState] = React.useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const {
    tokenRef: turnstileTokenRef,
    containerRef: turnstileContainerRef,
    containerClassName: turnstileClassName,
  } = useTurnstile();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    const config = getWindowJobsiteConfig();
    const turnstileToken = turnstileTokenRef.current;
    if (config?.turnstileSiteKey && !turnstileToken) {
      setFormState("error");
      return;
    }

    setFormState("submitting");

    try {
      if (config?.apiUrl && config?.siteId) {
        const params = new URLSearchParams(window.location.search);
        const isPreview = params.get("preview") === "true";
        const sessionId = isPreview ? null : sessionStorage.getItem("sp_session_id") || null;

        const result = await submitLead({
          apiUrl: config.apiUrl,
          siteId: config.siteId,
          backupApiUrl: config.backupApiUrl,
          businessName: config.business?.name,
          payload: {
            ...formData,
            variant: config?.variant,
            source: "hero-form",
            sessionId,
            ...(turnstileToken ? { turnstileToken } : {}),
          },
        });
        if (result.status === "failed") {
          throw new Error("Lead submission failed");
        }
      }

      setFormState("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
      window.setTimeout(() => setFormState("idle"), 5000);
    } catch {
      setFormState("error");
      window.setTimeout(() => setFormState("idle"), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <Card
      className={cn(
        "jobsite:overflow-hidden jobsite:border jobsite:shadow-2xl",
        variant === "clean" && "jobsite:border-border/80 jobsite:bg-background",
        variant === "panel" &&
          "jobsite:border-white/15 jobsite:bg-background/94 jobsite:backdrop-blur-xl",
        variant === "proof" && "jobsite:border-primary/10 jobsite:bg-background/98",
      )}
    >
      <CardContent className="jobsite:p-6 jobsite:sm:p-8">
        <div className="jobsite:mb-6 jobsite:space-y-2">
          <p className="jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-primary/70">
            {variant === "proof" ? "Fast response" : "Request service"}
          </p>
          <p className="jobsite:text-2xl jobsite:font-heading jobsite:text-card-foreground">
            Get pricing and availability
          </p>
          <p className="jobsite:text-sm jobsite:text-muted-foreground">
            Share a few details and we&apos;ll follow up quickly.
          </p>
        </div>

        {resolvingForm ? (
          <FormEmbedSkeleton />
        ) : customForm ? (
          <>
            <FormEmbed
              data={customForm}
              submitLabel={submitLabel}
              source="hero-form"
              hasTrackingPhone={hasTrackingPhone}
              businessSlug={businessSlug}
              buttonClassName={cn(variant === "proof" && "jobsite:rounded-full")}
            />
            <p className="jobsite:mt-4 jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground">
              No obligation. Fast response from a real team member.
            </p>
          </>
        ) : (
        <form className="jobsite:space-y-4" onSubmit={handleSubmit}>
          <div className="jobsite:grid jobsite:gap-4">
            <Input
              type="text"
              name="name"
              autoComplete="name"
              required
              value={formData.name}
              onChange={handleChange}
              disabled={formState === "submitting"}
              placeholder="Your name"
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />
            <Input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              disabled={formState === "submitting"}
              placeholder="Email address"
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />
            <Input
              type="tel"
              name="phone"
              autoComplete="tel"
              inputMode="tel"
              required
              value={formData.phone}
              onChange={handleChange}
              disabled={formState === "submitting"}
              placeholder="Phone number"
              className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
            />
            <Textarea
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              disabled={formState === "submitting"}
              placeholder="Tell us about your project"
              className="jobsite:resize-none jobsite:border-2 jobsite:text-base"
            />
          </div>

          <div ref={turnstileContainerRef} className={turnstileClassName} />

          <Button
            type="submit"
            disabled={formState === "submitting"}
            className={cn(
              "jobsite:h-12 jobsite:w-full jobsite:font-semibold",
              variant === "proof" && "jobsite:rounded-full",
            )}
            size="lg"
          >
            {formState === "submitting" ? "Sending..." : submitLabel}
          </Button>

          {formState === "success" && (
            <div className="jobsite:rounded-2xl jobsite:border jobsite:border-emerald-200 jobsite:bg-emerald-50 jobsite:p-4 jobsite:text-sm jobsite:font-medium jobsite:text-emerald-700">
              Thank you. We&apos;ll be in touch soon.
            </div>
          )}

          {formState === "error" && (
            <div className="jobsite:rounded-2xl jobsite:border jobsite:border-destructive/20 jobsite:bg-destructive/10 jobsite:p-4 jobsite:text-sm jobsite:font-medium jobsite:text-destructive">
              Something went wrong. Please try again.
            </div>
          )}

          <p className="jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground">
            No obligation. Fast response from a real team member.
          </p>

          {hasTrackingPhone && (
            <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground/70">
              By providing your phone number, you consent to receive calls and text messages (SMS).
              You can unsubscribe at any time by replying STOP. Message and data rates may apply.
            </p>
          )}
        </form>
        )}
      </CardContent>
    </Card>
  );
}

const DEFAULT_SLIDE_SECONDS = 6;

/**
 * Full-bleed photography that crossfades. Every slide stays mounted and opacity does the
 * work, so there is no layout shift and no reflow as photos load.
 */
function HeroSlideshow({
  photos,
  alt,
  paused,
  showDots,
  showPause,
  slideSeconds,
  motionClass,
}: {
  photos: HeroPhoto[];
  alt: string;
  paused: boolean;
  showDots: boolean;
  showPause: boolean;
  slideSeconds: number;
  motionClass: string;
}) {
  // Reduced motion stops the rotation via the hook, holding the current frame; the
  // dots still navigate manually.
  const { index, goTo, playing, setPlaying, prefersReducedMotion } = useSlideshow({
    count: photos.length,
    intervalMs: slideSeconds * 1000,
    paused,
  });
  // Nothing is moving under reduced motion, so a pause control would be a lie.
  const showsPauseControl = showPause && photos.length > 1 && !prefersReducedMotion;

  return (
    <>
      <div className="jobsite:absolute jobsite:inset-0" aria-hidden="true">
        {photos.map((photo, slideIndex) => (
          <img
            key={`${photo.imageUrl}-${slideIndex}`}
            src={photo.imageUrl}
            alt={slideIndex === 0 ? alt : ""}
            crossOrigin="anonymous"
            // Only the first slide is part of LCP; the rest must not compete with it.
            fetchPriority={slideIndex === 0 ? "high" : "low"}
            loading={slideIndex === 0 ? "eager" : "lazy"}
            style={imagePositionStyle(photo.position)}
            className={cn(
              "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover jobsite:object-center jobsite:transition-opacity jobsite:duration-1000 jobsite:ease-in-out",
              slideIndex === index ? "jobsite:opacity-100" : "jobsite:opacity-0",
              motionClass,
            )}
          />
        ))}
      </div>
      {showsPauseControl ? (
        // Bottom-right: clear of the copy block (bottom-left) and the dots
        // (bottom-centre). Quiet enough not to fight the photography.
        <button
          type="button"
          onClick={() => setPlaying(!playing)}
          aria-label={playing ? "Pause slideshow" : "Play slideshow"}
          // Lets the editor canvas run this click instead of selecting the
          // section, so the control is testable where it's configured.
          data-sp-interactive=""
          // z-20, above the copy block: it is z-10, full width, bottom
          // anchored and later in the DOM, so at equal z it covers the
          // controls and swallows their clicks.
          className="jobsite:absolute jobsite:bottom-4 jobsite:right-4 jobsite:z-20 jobsite:flex jobsite:h-8 jobsite:w-8 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-black/25 jobsite:text-white/90 jobsite:backdrop-blur-sm jobsite:transition-colors jobsite:hover:bg-black/45 jobsite:hover:text-white"
        >
          {playing ? (
            <Pause className="jobsite:h-3.5 jobsite:w-3.5" />
          ) : (
            <Play className="jobsite:h-3.5 jobsite:w-3.5" />
          )}
        </button>
      ) : null}
      {showDots && photos.length > 1 ? (
        // The row spans the full width, so it stays click-through and only the
        // dots themselves take pointer events.
        <div className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:bottom-4 jobsite:z-20 jobsite:flex jobsite:justify-center jobsite:gap-1">
          {photos.map((photo, dotIndex) => (
            <button
              key={`dot-${photo.imageUrl}-${dotIndex}`}
              type="button"
              onClick={() => goTo(dotIndex)}
              aria-label={`Show photo ${dotIndex + 1} of ${photos.length}`}
              aria-current={dotIndex === index}
              data-sp-interactive=""
              className="jobsite:pointer-events-auto jobsite:flex jobsite:h-6 jobsite:w-6 jobsite:items-center jobsite:justify-center"
            >
              <span
                className={cn(
                  "jobsite:h-2 jobsite:w-2 jobsite:rounded-full jobsite:transition-colors jobsite:duration-300",
                  dotIndex === index ? "jobsite:bg-white" : "jobsite:bg-white/45",
                )}
              />
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}

export const Hero: React.FC<HeroProps> = ({
  headline,
  subheadline,
  ctaText,
  ctaUrl,
  secondaryCtaText,
  secondaryCtaUrl,
  trustIndicators,
  backgroundImage,
  photos,
  showCopy = "yes",
  showSlideshowDots = "no",
  showSlideshowPause = "no",
  imageMotion = "none",
  slideSeconds = DEFAULT_SLIDE_SECONDS,
  imagePosition = "center",
  overlay = "dark",
  textColor = "auto",
  layout = "centered",
  variant = "clean",
  keywordBadge,
  hasTrackingPhone,
  heroForm = "lead",
  bookingSlug,
  bookingApiUrl,
  businessName,
  className,
  puck,
}: HeroProps & {
  puck?: { metadata?: { business?: { phone?: string; telephone?: string } } };
}) => {
  const metadataPhone = puck?.metadata?.business?.phone || puck?.metadata?.business?.telephone;
  const resolvedSecondaryCtaText = metadataPhone
    ? `Call Us: ${formatPhoneDisplay(metadataPhone)}`
    : secondaryCtaText;
  // A transparent site nav is fixed and overlays whatever the page starts with,
  // so every hero layout hosts it: extra top padding for the nav's height plus a
  // top scrim so its text stays legible over the photo. Same signal
  // EmergencyBand already uses.
  const runtimeConfig = getWindowJobsiteConfig();
  const navIsTransparent =
    (runtimeConfig?.navigation?.style ?? runtimeConfig?.navigationStyle) === "transparent";

  const resolvedSecondaryCtaUrl = metadataPhone
    ? `tel:${metadataPhone.replace(/\D/g, "")}`
    : secondaryCtaUrl;

  const [showcasePaused, setShowcasePaused] = React.useState(false);
  // Showcase sits on its own photos, so they are what the text has to stay legible against.
  const showcaseFirstPhoto = (photos || []).find((photo) => photo?.imageUrl)?.imageUrl;
  const lightText = resolveLightText(
    textColor,
    layout === "showcase" ? showcaseFirstPhoto || backgroundImage : backgroundImage,
    overlay,
  );
  // Tolerate a ReactNode in `text`: the dashboard section editor swaps the
  // string for an inline-editable element. Strings keep the exact old
  // semantics (whitespace-only rows drop).
  const validTrustIndicators = (trustIndicators || []).filter((item) =>
    typeof item.text === "string" ? item.text.trim() : item.text,
  );
  const panelSurfaceClass = cn(
    "jobsite:mx-auto jobsite:max-w-5xl jobsite:border jobsite:px-6 jobsite:py-8 jobsite:shadow-2xl jobsite:sm:px-8 jobsite:md:px-10 jobsite:md:py-10",
    lightText
      ? "jobsite:border-white/15 jobsite:bg-black/25 jobsite:backdrop-blur-xl"
      : "jobsite:border-border/80 jobsite:bg-background/94 jobsite:backdrop-blur-md",
  );
  const headlineTextClass = lightText
    ? "jobsite:text-white jobsite:[text-shadow:0_2px_18px_rgba(0,0,0,0.34)]"
    : "jobsite:text-foreground";
  const bodyTextClass = lightText
    ? "jobsite:text-white/88 jobsite:[text-shadow:0_1px_12px_rgba(0,0,0,0.28)]"
    : "jobsite:text-muted-foreground";
  const emphasisTextClass = lightText ? "jobsite:text-white" : "jobsite:text-foreground";
  const contentWidthClass = variant === "proof" ? "jobsite:max-w-5xl" : "jobsite:max-w-6xl";
  const primaryButtonClass = cn(
    "jobsite:w-full jobsite:sm:w-auto jobsite:px-8 jobsite:py-6 jobsite:text-base jobsite:font-semibold",
    variant === "proof" && "jobsite:shadow-lg",
    lightText
      ? "jobsite:bg-background jobsite:text-foreground jobsite:hover:bg-background/90"
      : "jobsite:bg-primary jobsite:text-primary-foreground jobsite:hover:bg-primary/90",
  );
  const secondaryButtonClass = cn(
    "jobsite:w-full jobsite:sm:w-auto jobsite:px-8 jobsite:py-6 jobsite:text-base jobsite:font-semibold",
    lightText
      ? "jobsite:border-white/40 jobsite:bg-black/10 jobsite:backdrop-blur-md jobsite:text-white jobsite:hover:bg-black/20"
      : "jobsite:border-foreground/15 jobsite:bg-transparent jobsite:text-foreground jobsite:hover:bg-foreground/5",
  );
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;

  const renderTrustIndicators = (centered: boolean) => {
    if (validTrustIndicators.length === 0) return null;

    return (
      <div
        className={cn(
          "jobsite:flex jobsite:max-w-3xl jobsite:flex-wrap jobsite:gap-x-6 jobsite:gap-y-3",
          centered ? "jobsite:mx-auto jobsite:justify-center" : "jobsite:justify-start",
        )}
      >
        {validTrustIndicators.map((indicator, idx) => (
          <div
            key={idx}
            className={cn(
              "jobsite:inline-flex jobsite:items-center jobsite:gap-3 jobsite:py-1",
              centered ? "jobsite:justify-center" : "jobsite:justify-start",
            )}
          >
            <span
              className={cn(
                "jobsite:flex jobsite:h-8 jobsite:w-8 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:rounded-full",
                lightText
                  ? "jobsite:border jobsite:border-white/12 jobsite:bg-white/10"
                  : variant === "clean"
                    ? "jobsite:bg-muted"
                    : "jobsite:bg-primary/10",
              )}
            >
              <CheckCircle
                className={cn(
                  "jobsite:h-4 jobsite:w-4",
                  lightText ? "jobsite:text-primary-foreground" : "jobsite:text-primary",
                )}
              />
            </span>
            <span
              className={cn(
                "jobsite:text-sm jobsite:font-semibold jobsite:tracking-tight jobsite:sm:text-[0.96rem]",
                emphasisTextClass,
              )}
            >
              {indicator.text}
            </span>
          </div>
        ))}
      </div>
    );
  };


  if (layout === "showcase") {
    const slides = (photos || []).filter((photo) => photo?.imageUrl);
    const showcasePhotos =
      slides.length > 0 ? slides : backgroundImage ? [{ imageUrl: backgroundImage }] : [];
    const hasPhotos = showcasePhotos.length > 0;
    const showsCopy = showCopy !== "no";

    return (
      // The pause surface is the COPY block below, not the whole section: on a
      // full-screen hero the pointer is almost always inside the section, so a
      // section-wide hover-pause read as "the slideshow is broken". Reading or
      // tabbing through the copy still stills the background behind it;
      // photography-only heroes never pause (reduced-motion still stops them).
      <section
        className={cn(
          // Phones stop short of full height so the next section peeks: a
          // full-screen photo with nothing under it reads as the end of the
          // page, and the peek is a better scroll cue than any chevron.
          "jobsite:relative jobsite:flex jobsite:min-h-[80svh] jobsite:md:min-h-[100svh] jobsite:overflow-hidden",
          // The frame tells its children they are over photography, so an
          // element set to `auto` comes out legible without configuration.
          hasPhotos && FRAME_ON_MEDIA_CLASS,
          showsCopy ? "jobsite:items-end" : "jobsite:items-center",
          !hasPhotos &&
            "jobsite:bg-linear-to-br jobsite:from-primary jobsite:via-primary jobsite:to-primary/90",
          className,
        )}
      >
        {hasPhotos && (
          <HeroSlideshow
            photos={showcasePhotos}
            alt={headline}
            paused={showcasePaused}
            showDots={showSlideshowDots === "yes"}
            showPause={showSlideshowPause === "yes"}
            slideSeconds={slideSeconds || DEFAULT_SLIDE_SECONDS}
            motionClass={imageMotionClass(imageMotion)}
          />
        )}

        {/* A short scrim at the top only, so a transparent nav stays legible on a bright sky. */}
        {hasPhotos && (
          <div
            className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:top-0 jobsite:h-32 jobsite:bg-linear-to-b jobsite:from-black/35 jobsite:to-transparent"
            aria-hidden="true"
          />
        )}

        {hasPhotos && showsCopy && variant !== "panel" && (
          <div
            className={cn(
              "jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:bottom-0 jobsite:h-2/3 jobsite:bg-linear-to-t",
              getShowcaseScrimClass(overlay),
            )}
            aria-hidden="true"
          />
        )}

        {/*
          Hiding the copy must not cost the page its only h1 — a homepage whose hero is pure
          photography still needs a heading for search engines and screen readers.
        */}
        {!showsCopy && <h1 className="jobsite:sr-only">{headline}</h1>}

        {/*
          A fixed inset from the screen edge, not the shared centred container. On a wide
          monitor the container's auto margins pushed the copy hundreds of pixels inward, so
          it read as floating in the middle of the photo rather than anchored to a corner.
        */}
        {showsCopy && (
          <div
            // Keyboard only: tabbing through the hero CTAs shouldn't happen
            // against a moving background. Hover deliberately does NOT pause —
            // on a full-screen hero the pointer is nearly always inside it, so
            // hover-pause made the slideshow look broken.
            onFocusCapture={() => setShowcasePaused(true)}
            onBlurCapture={() => setShowcasePaused(false)}
            className="jobsite:relative jobsite:z-10 jobsite:w-full jobsite:px-6 jobsite:pb-14 jobsite:sm:px-10 jobsite:md:pb-20 jobsite:lg:px-16"
          >
            <div
              className={cn(
                "jobsite:max-w-3xl jobsite:space-y-5",
                variant === "panel" && panelSurfaceClass,
              )}
              style={variant === "panel" ? surfaceRadiusStyle : undefined}
            >
              {keywordBadge && (
                <span
                  className={cn(
                    "jobsite:inline-flex jobsite:px-4 jobsite:py-1.5 jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:shadow-lg",
                    lightText
                      ? "jobsite:border jobsite:border-white/28 jobsite:bg-black/15 jobsite:backdrop-blur-md jobsite:text-white"
                      : "jobsite:border jobsite:border-accent/25 jobsite:bg-accent/15 jobsite:text-accent",
                  )}
                  style={surfaceRadiusStyle}
                >
                  {keywordBadge}
                </span>
              )}

              <h1
                className={cn(
                  "jobsite:font-heading jobsite:text-4xl jobsite:leading-[1.1] jobsite:tracking-tight jobsite:text-balance jobsite:sm:text-5xl jobsite:md:text-6xl",
                  headlineTextClass,
                )}
              >
                {headline}
              </h1>
              {subheadline && (
                <p
                  className={cn(
                    "jobsite:max-w-2xl jobsite:text-lg jobsite:leading-relaxed jobsite:sm:text-xl",
                    bodyTextClass,
                  )}
                >
                  {subheadline}
                </p>
              )}
              <div className="jobsite:flex jobsite:flex-col jobsite:gap-3 jobsite:sm:flex-row">
                <Button asChild size="lg" className={primaryButtonClass}>
                  <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
                </Button>
                {resolvedSecondaryCtaText && resolvedSecondaryCtaUrl && (
                  <Button asChild variant="outline" size="lg" className={secondaryButtonClass}>
                    <SmartLink to={resolvedSecondaryCtaUrl}>{resolvedSecondaryCtaText}</SmartLink>
                  </Button>
                )}
              </div>

              {/* Proof is the variant that leads with credentials, here as everywhere else. */}
              {variant === "proof" && renderTrustIndicators(false)}
            </div>
          </div>
        )}
      </section>
    );
  }

  if (layout === "centered") {
    return (
      <section
        className={cn(
          "jobsite:relative jobsite:overflow-hidden jobsite:pb-20 jobsite:md:pb-32",
          backgroundImage && FRAME_ON_MEDIA_CLASS,
          navIsTransparent
            ? "jobsite:pt-36 jobsite:md:pt-48"
            : "jobsite:pt-20 jobsite:md:pt-32",
          !backgroundImage &&
            "jobsite:bg-linear-to-br jobsite:from-primary jobsite:via-primary jobsite:to-primary/90",
          className,
        )}
      >
        {backgroundImage && (
          <img
            src={backgroundImage}
            alt=""
            crossOrigin="anonymous"
            fetchPriority="high"
            style={imagePositionStyle(imagePosition)}
            className={cn(
              "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover jobsite:object-center",
              imageMotionClass(imageMotion),
            )}
            aria-hidden="true"
          />
        )}
        {backgroundImage && (
          <div
            className="jobsite:absolute jobsite:inset-0"
            style={{ backgroundImage: getOverlayStyles(overlay) }}
            aria-hidden="true"
          />
        )}
        {navIsTransparent && backgroundImage && (
          <div
            className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:top-0 jobsite:h-32 jobsite:bg-linear-to-b jobsite:from-black/35 jobsite:to-transparent"
            aria-hidden="true"
          />
        )}

        <div
          className={cn(
            "jobsite:container jobsite:relative jobsite:z-10 jobsite:mx-auto jobsite:px-4 jobsite:text-center",
            contentWidthClass,
          )}
        >
          <div className={cn(variant === "panel" && panelSurfaceClass)}>
            <div className="jobsite:space-y-6">
              {keywordBadge && (
                <div className="jobsite:flex jobsite:justify-center">
                  <span
                    className={cn(
                      "jobsite:inline-flex jobsite:px-4 jobsite:py-1.5 jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:shadow-lg",
                      lightText
                        ? "jobsite:border jobsite:border-white/28 jobsite:bg-black/15 jobsite:backdrop-blur-md jobsite:text-white"
                        : "jobsite:border jobsite:border-accent/25 jobsite:bg-accent/15 jobsite:text-accent",
                    )}
                    style={surfaceRadiusStyle}
                  >
                    {keywordBadge}
                  </span>
                </div>
              )}

              <h1
                className={cn(
                  "jobsite:mx-auto jobsite:max-w-5xl jobsite:font-heading jobsite:text-4xl jobsite:leading-[1.1] jobsite:tracking-tight jobsite:text-balance jobsite:sm:text-5xl jobsite:md:text-6xl jobsite:xl:text-7xl",
                  headlineTextClass,
                )}
              >
                {headline}
              </h1>

              <p
                className={cn(
                  "jobsite:mx-auto jobsite:max-w-3xl jobsite:text-lg jobsite:leading-relaxed jobsite:sm:text-xl jobsite:md:text-2xl",
                  bodyTextClass,
                )}
              >
                {subheadline}
              </p>

              <div className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:gap-3 jobsite:sm:flex-row">
                <Button asChild size="lg" className={primaryButtonClass}>
                  <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
                </Button>
                {resolvedSecondaryCtaText && resolvedSecondaryCtaUrl && (
                  <Button asChild variant="outline" size="lg" className={secondaryButtonClass}>
                    <SmartLink to={resolvedSecondaryCtaUrl}>{resolvedSecondaryCtaText}</SmartLink>
                  </Button>
                )}
              </div>

              {renderTrustIndicators(true)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn(
        "jobsite:relative jobsite:flex jobsite:min-h-[620px] jobsite:items-center jobsite:overflow-hidden jobsite:pb-14 jobsite:md:pb-20 jobsite:xl:min-h-[700px]",
        backgroundImage && FRAME_ON_MEDIA_CLASS,
        navIsTransparent ? "jobsite:pt-30 jobsite:md:pt-36" : "jobsite:pt-14 jobsite:md:pt-20",
        !backgroundImage &&
          "jobsite:bg-linear-to-br jobsite:from-primary jobsite:via-primary jobsite:to-primary/90",
        className,
      )}
    >
      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          crossOrigin="anonymous"
          fetchPriority="high"
          style={imagePositionStyle(imagePosition)}
          className={cn(
            "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover jobsite:object-center",
            imageMotionClass(imageMotion),
          )}
          aria-hidden="true"
        />
      )}
      {navIsTransparent && backgroundImage && (
        <div
          className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:top-0 jobsite:h-32 jobsite:bg-linear-to-b jobsite:from-black/35 jobsite:to-transparent"
          aria-hidden="true"
        />
      )}
      {backgroundImage && (
        <div
          className="jobsite:absolute jobsite:inset-0"
          style={{ backgroundImage: getOverlayStyles(overlay) }}
          aria-hidden="true"
        />
      )}

      <div className="jobsite:container jobsite:relative jobsite:z-10 jobsite:mx-auto jobsite:w-full jobsite:px-4 jobsite:md:px-6 jobsite:lg:px-8">
        <div className="jobsite:grid jobsite:grid-cols-1 jobsite:items-center jobsite:gap-8 jobsite:lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] jobsite:xl:gap-16">
          <div className="jobsite:space-y-6">
            <div
              className={cn(
                "jobsite:space-y-5",
                variant === "panel" &&
                  "jobsite:max-w-2xl jobsite:border jobsite:px-6 jobsite:py-6 jobsite:shadow-2xl jobsite:sm:px-8 jobsite:sm:py-8",
                variant === "panel" &&
                  (lightText
                    ? "jobsite:border-white/15 jobsite:bg-black/20 jobsite:backdrop-blur-xl"
                    : "jobsite:border-border/80 jobsite:bg-background/94"),
              )}
              style={variant === "panel" ? surfaceRadiusStyle : undefined}
            >
              {keywordBadge && (
                <span
                  className={cn(
                    "jobsite:inline-flex jobsite:px-4 jobsite:py-1.5 jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:shadow-lg",
                    lightText
                      ? "jobsite:border jobsite:border-white/28 jobsite:bg-black/15 jobsite:backdrop-blur-md jobsite:text-white"
                      : "jobsite:border jobsite:border-accent/25 jobsite:bg-accent/15 jobsite:text-accent",
                  )}
                  style={surfaceRadiusStyle}
                >
                  {keywordBadge}
                </span>
              )}

              <h1
                className={cn(
                  "jobsite:max-w-2xl jobsite:font-heading jobsite:text-4xl jobsite:leading-[1.1] jobsite:tracking-tight jobsite:text-balance jobsite:sm:text-5xl jobsite:xl:text-6xl",
                  headlineTextClass,
                )}
              >
                {headline}
              </h1>

              <p
                className={cn(
                  "jobsite:max-w-2xl jobsite:text-lg jobsite:leading-relaxed jobsite:sm:text-xl jobsite:xl:text-2xl",
                  bodyTextClass,
                )}
              >
                {subheadline}
              </p>
            </div>

            <div className="jobsite:flex jobsite:flex-col jobsite:gap-3 jobsite:sm:flex-row">
              <Button asChild size="lg" className={primaryButtonClass}>
                <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
              </Button>
              {resolvedSecondaryCtaText && resolvedSecondaryCtaUrl && (
                <Button asChild variant="outline" size="lg" className={secondaryButtonClass}>
                  <SmartLink to={resolvedSecondaryCtaUrl}>{resolvedSecondaryCtaText}</SmartLink>
                </Button>
              )}
            </div>

            {renderTrustIndicators(false)}
          </div>

          <div className="jobsite:relative jobsite:z-10 jobsite:xl:pl-6">
            {heroForm === "booking" ? (
              <BookingWidgetInline
                slug={bookingSlug}
                apiUrl={bookingApiUrl}
                businessName={businessName}
                businessPhone={metadataPhone}
              />
            ) : (
              <HeroLeadCaptureCard
                variant={variant}
                hasTrackingPhone={hasTrackingPhone}
                submitLabel={ctaText}
                businessSlug={bookingSlug}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
